import { keyBy } from 'es-toolkit';
import React, { ReactElement, useMemo, useState } from 'react';
import { EditingState } from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableColumnResizing,
  TableEditColumn,
  TableEditRow,
  TableHeaderRow,
  TableInlineCellEditing,
} from '@devexpress/dx-react-grid-material-ui';
import { ButtonProps, Paper, Typography } from '@mui/material';

import { MiscellaneousItem } from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

const miscellaneousItemTableColumns: {
  name: keyof MiscellaneousItem;
  title: string;
  defaultWidth: number;
  required: boolean;
}[] = [
  { name: 'item', title: 'Item', defaultWidth: 200, required: true },
  { name: 'quantity', title: 'Qty.', defaultWidth: 60, required: true },
  { name: 'notes', title: 'Notes (optional)', defaultWidth: 400, required: false },
];

// A version of `ChangeSet` from dx-react-grid that uses `unknown` instead of `any`
interface UnknownChangeSet {
  added?: readonly unknown[];
  changed?: {
    [key: string]: unknown;
  };
  deleted?: readonly (number | string)[];
}

interface MiscellaneousItemsProps {
  readonly miscellaneousItems: readonly MiscellaneousItem[];
  readonly setMiscellaneousItems: (miscellaneousItems: readonly MiscellaneousItem[]) => void;
  readonly setParentSubmitDisabled: (parentSubmitDisabled: boolean) => void;
  readonly disabled?: boolean;
}

export function MiscellaneousItemsTable(props: MiscellaneousItemsProps) {
  const { miscellaneousItems, setMiscellaneousItems, setParentSubmitDisabled } = props;

  const miscellaneousItemsByGuid = useMemo(
    () => keyBy(miscellaneousItems, (miscellaneousItem) => miscellaneousItem.guid),
    [miscellaneousItems],
  );

  const [editingRowIds, setEditingRowIds] = useState<(string | number)[]>([]);
  const [addedRows, setAddedRows] = useState<MiscellaneousItem[]>([]);
  const [rowChanges, setRowChanges] = useState({});
  const [addedRowIdsWithMissingInfo, setAddedRowIdsWithMissingInfo] = useState<readonly string[]>([]);

  function commitChanges({ added, changed, deleted }: UnknownChangeSet) {
    const anyChanges = !!added || (changed && Object.values(changed).some(notNullish)) || !!deleted;
    if (!anyChanges) return;

    let updatedMiscellaneousItems = [...miscellaneousItems];
    if (added) {
      updatedMiscellaneousItems = updatedMiscellaneousItems.concat(
        added.map((row) => createMiscellaneousItem(row as MaybeMiscellaneousItem)),
      );
    }
    if (changed && Object.values(changed).some(notNullish)) {
      updatedMiscellaneousItems = updatedMiscellaneousItems.map((miscellaneousItem) =>
        changed[miscellaneousItem.guid]
          ? applyChanges(miscellaneousItem, changed[miscellaneousItem.guid] as MaybeMiscellaneousItem)
          : miscellaneousItem,
      );
    }
    if (deleted) {
      const deletedSet = new Set(deleted);
      updatedMiscellaneousItems = updatedMiscellaneousItems.filter(
        (miscellaneousItem) => !deletedSet.has(miscellaneousItem.guid),
      );
    }

    // If any changes are invalid, rollback to the original value
    updatedMiscellaneousItems = updatedMiscellaneousItems.map((updatedMiscellaneousItem) =>
      validateMiscellaneousItem(updatedMiscellaneousItem)
        ? updatedMiscellaneousItem
        : miscellaneousItemsByGuid[updatedMiscellaneousItem.guid],
    );

    setMiscellaneousItems(updatedMiscellaneousItems);
  }

  function changeAddedRows(rows: readonly unknown[]) {
    const initialized = rows.map((row) => createMiscellaneousItem(row as MaybeMiscellaneousItem));
    const rowGuidsWithMissingOrInvalidInfo = initialized
      .filter((miscellaneousItem) => !validateMiscellaneousItem(miscellaneousItem))
      .map((row) => row.guid);
    setAddedRowIdsWithMissingInfo(rowGuidsWithMissingOrInvalidInfo);
    setAddedRows(initialized);
    setParentSubmitDisabled(initialized.length !== 0);
  }

  return (
    <>
      <Typography variant="h6">Miscellaneous Items</Typography>
      <Typography>
        Hangers from Design will be included on the bill of lading already. You do not need to list them here.
      </Typography>
      <Paper>
        <Grid
          rows={miscellaneousItems}
          columns={miscellaneousItemTableColumns}
          getRowId={(miscellaneousItem) => miscellaneousItem.guid}
        >
          <EditingState
            onCommitChanges={commitChanges}
            rowChanges={rowChanges}
            onRowChangesChange={(rowChanges) => {
              // This conditional helps tab ordering work as expected.
              if (Object.keys(rowChanges).length > 0) setRowChanges(rowChanges);
            }}
            editingRowIds={editingRowIds}
            onEditingRowIdsChange={setEditingRowIds}
            addedRows={addedRows}
            onAddedRowsChange={changeAddedRows}
          />
          <Table cellComponent={FocusableCell} />
          <TableColumnResizing
            defaultColumnWidths={miscellaneousItemTableColumns.map((column) => ({
              columnName: column.name,
              width: column.defaultWidth,
            }))}
          />
          <TableHeaderRow />
          <TableInlineCellEditing startEditAction="click" selectTextOnEditStart />
          <TableEditRow />
          <TableEditColumn
            showAddCommand
            showDeleteCommand
            cellComponent={(props) => (
              <EditCommandsCell disableSave={addedRowIdsWithMissingInfo.includes(props.row.guid)} {...props} />
            )}
            headerCellComponent={(props) => (
              <TableEditColumn.HeaderCell {...props}>
                {React.Children.map(props.children, (child) =>
                  child ? React.cloneElement(child as ReactElement<ButtonProps>, { variant: 'outlined' }) : child,
                )}
              </TableEditColumn.HeaderCell>
            )}
          />
        </Grid>
      </Paper>
    </>
  );
}

/**
 * Derived from https://devexpress.github.io/devextreme-reactive/react/grid/docs/guides/editing/#inline-cell-editing
 */
function FocusableCell({ onClick, ...restProps }: Table.DataCellProps & { onClick?: () => void }) {
  return <Table.Cell {...restProps} tabIndex={0} onFocus={onClick} />;
}

/**
 * Derived from https://devexpress.github.io/devextreme-reactive/react/grid/docs/guides/editing/#data-validation
 */
function EditCommandsCell({ disableSave, ...props }: TableEditColumn.CellProps & { disableSave: boolean }) {
  const { children } = props;
  return (
    <TableEditColumn.Cell {...props}>
      {React.Children.map(children, (child) =>
        child && (child as ReactElement<ButtonProps>).props.id === 'commit'
          ? React.cloneElement(child as ReactElement<ButtonProps>, { disabled: disableSave, variant: 'outlined' })
          : child
            ? React.cloneElement(child as ReactElement<ButtonProps>, { variant: 'outlined' })
            : null,
      )}
    </TableEditColumn.Cell>
  );
}

type MaybeMiscellaneousItem = Record<keyof MiscellaneousItem, unknown>;

function createMiscellaneousItem(row: MaybeMiscellaneousItem): MiscellaneousItem {
  return {
    guid: typeof row.guid === 'string' ? row.guid : crypto.randomUUID(),
    item: typeof row.item === 'string' ? row.item : '',
    quantity: typeof row.quantity === 'string' ? row.quantity : '',
    notes: typeof row.notes === 'string' ? row.notes : null,
  };
}

function applyChanges(miscellaneousItem: MiscellaneousItem, change: MaybeMiscellaneousItem): MiscellaneousItem {
  return {
    guid: miscellaneousItem.guid,
    item: typeof change.item === 'string' ? change.item : miscellaneousItem.item,
    quantity: typeof change.quantity === 'string' ? change.quantity : miscellaneousItem.quantity,
    notes: typeof change.notes === 'string' ? change.notes : miscellaneousItem.notes,
  };
}

function validateMiscellaneousItem(miscellaneousItem: MiscellaneousItem) {
  if (miscellaneousItem.item.trim() === '') return false;
  if (miscellaneousItem.quantity.trim() === '') return false;
  return true;
}
